import React from 'react';
import { formatDateToDisplayString } from 'utils';
import { ANAMNESIS_SECTION_SEPARATOR } from 'utils/anamnesis';
import {
  AdditionalFilesNotice,
  AnamnesisAccordionContent,
  AnamnesisAccordionItem,
  AnamnesisAccordionTrigger,
  AnamnesisCard,
  AnamnesisLabel,
  AnamnesisRow,
  SummaryItemRed,
  SummarySectionContent,
  SummarySectionHeading,
  SummarySectionsList,
  VisitReasonLine,
} from './BesuchsgrundSection.styles';
import { Accordion } from 'components';
import { t } from 'i18next';
import type {
  AnamnesisSummaryContent,
  AnamnesisSummaryItem,
  AnamnesisSummarySection,
} from 'types/anamnesis';
import { useSetAnamnesisSeen } from 'hooks';

type SummarySectionProps = {
  summary: AnamnesisSummaryContent | null | undefined;
  mode?: 'card' | 'accordion';
  collapsibleSummary?: boolean;
};

function renderItems(items: AnamnesisSummaryItem[]): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  items.forEach((item, index) => {
    if (!item.text) return;
    if (nodes.length > 0) {
      nodes.push(item.attachedToPrevious ? ' ' : ANAMNESIS_SECTION_SEPARATOR);
    }
    const content =
      item.displayType === 'red' ? (
        <SummaryItemRed key={`item-${index}`}>{item.text}</SummaryItemRed>
      ) : (
        <React.Fragment key={`item-${index}`}>{item.text}</React.Fragment>
      );
    nodes.push(content);
  });
  return nodes;
}

function hasContent(summary: AnamnesisSummaryContent | null | undefined): boolean {
  if (!summary) return false;
  if (summary.hasAdditionalFiles) return true;
  return summary.sections.some((section) => section.items.some((item) => item.text));
}

const SummaryBody: React.FC<{ summary: AnamnesisSummaryContent }> = ({ summary }) => (
  <SummarySectionsList>
    {summary.sections.map((section: AnamnesisSummarySection, sectionIndex: number) => {
      const itemNodes = renderItems(section.items);
      if (itemNodes.length === 0) return null;
      return (
        <React.Fragment key={`section-${sectionIndex}`}>
          <SummarySectionHeading>{section.heading}</SummarySectionHeading>
          <SummarySectionContent>{itemNodes}</SummarySectionContent>
        </React.Fragment>
      );
    })}
    {summary.hasAdditionalFiles ? (
      <AdditionalFilesNotice>{t('recording.additionalFilesNotice')}</AdditionalFilesNotice>
    ) : null}
  </SummarySectionsList>
);

export const SummarySection = ({
  summary,
  mode = 'card',
  collapsibleSummary = false,
}: SummarySectionProps) => {
  const { mutate: setSeen } = useSetAnamnesisSeen();
  const formattedDate = summary?.date ? formatDateToDisplayString(summary?.date) : '';

  const handleAccordionChange = (value: string) => {
    if (value || !summary || summary.seen || !summary.id) return;
    setSeen({ id: summary.id, data: { seen: true } });
  };

  if (!hasContent(summary) || !summary) {
    return null;
  }

  if (mode === 'accordion') {
    return (
      <Accordion
        type="single"
        collapsible
        className="w-full"
        defaultValue={collapsibleSummary ? undefined : 'selfAnamnesis'}
        onValueChange={handleAccordionChange}
      >
        <AnamnesisAccordionItem value="selfAnamnesis">
          <AnamnesisAccordionTrigger data-ph-no-autocapture>
            {t('recording.selectedAnamnesis')}
            {formattedDate ? ` (${formattedDate})` : ''}:
          </AnamnesisAccordionTrigger>
          <AnamnesisAccordionContent>
            <SummaryBody summary={summary} />
          </AnamnesisAccordionContent>
        </AnamnesisAccordionItem>
      </Accordion>
    );
  }

  return (
    <AnamnesisCard>
      <AnamnesisRow>
        <AnamnesisLabel>
          {t('recording.selectedAnamnesis')}
          {formattedDate ? ` (${formattedDate})` : ''}:
        </AnamnesisLabel>
        <VisitReasonLine $stacked>
          <SummaryBody summary={summary} />
        </VisitReasonLine>
      </AnamnesisRow>
    </AnamnesisCard>
  );
};
